import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import type { UseMutationResult } from '@tanstack/react-query';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { Button, Input, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { s3ExtractError } from '@/api/s3';
import { S3ConsoleBreadcrumbs } from '../S3ConsoleBreadcrumbs';
import { iamEntityColumns } from './iam-entity-columns';

/**
 * Generic IAM list screen (users / groups / roles). All three share the same
 * name/created table, create button, and delete-confirm flow — only the labels,
 * route resource segment and data hooks differ, so one component drives them all
 * while each resource keeps a thin, self-documenting page file.
 */
export function IamListPage<T>({
  serviceId,
  resource,
  title,
  description,
  createLabel,
  entities,
  isLoading,
  isError,
  refetch,
  nameOf,
  dateOf,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  del,
  entityLabel,
}: {
  serviceId: string;
  /** URL/API resource segment: 'users' | 'groups' | 'roles'. */
  resource: string;
  title: string;
  description: string;
  createLabel: string;
  entities: T[];
  isLoading: boolean;
  isError?: boolean;
  refetch?: () => void;
  nameOf: (row: T) => string;
  dateOf: (row: T) => string | null | undefined;
  emptyIcon: ReactNode;
  emptyTitle: string;
  emptyDescription: string;
  del: UseMutationResult<void, Error, string>;
  /** Singular noun for toasts/dialogs, e.g. 'user'. */
  entityLabel: string;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [toDelete, setToDelete] = useState<T | null>(null);
  const [search, setSearch] = useState('');
  const base = `/services/oss/${serviceId}/iam/${resource}`;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? entities.filter((e) => nameOf(e).toLowerCase().includes(q)) : entities;
  }, [entities, search, nameOf]);

  // Names may carry reserved URL chars — encode into the detail route.
  const onView = (row: T) => navigate(`${base}/${encodeURIComponent(nameOf(row))}`);

  const confirmDelete = () => {
    if (!toDelete) return;
    del.mutate(nameOf(toDelete), {
      onSuccess: () => {
        toast.success(`${title.replace(/s$/, '')} deleted`);
        setToDelete(null);
      },
      onError: (e) => {
        toast.error(s3ExtractError(e, `Failed to delete ${entityLabel}`));
        setToDelete(null);
      },
    });
  };

  const columns = iamEntityColumns<T>({ nameOf, dateOf, onView, onDelete: setToDelete });

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<S3ConsoleBreadcrumbs trail={[{ label: title }]} />}
        title={`${title}${entities.length ? ` (${entities.length})` : ''}`}
        description={description}
        actions={
          <Button
            variant="primary"
            startIcon={<Plus size={15} />}
            onClick={() => navigate(`${base}/create`)}
          >
            {createLabel}
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder={`Search ${entityLabel}s…`}
            startSlot={<Search size={15} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title={`Couldn't load ${entityLabel}s`}
          actions={
            refetch ? (
              <Button size="sm" variant="secondary" onClick={refetch}>
                Retry
              </Button>
            ) : undefined
          }
        >
          The storage service is temporarily unavailable.
        </Alert>
      ) : isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={44} />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => nameOf(r)}
          emptyState={
            entities.length === 0 ? (
              <EmptyState
                icon={emptyIcon}
                title={emptyTitle}
                description={emptyDescription}
                actions={
                  <Button size="sm" startIcon={<Plus size={14} />} onClick={() => navigate(`${base}/create`)}>
                    {createLabel}
                  </Button>
                }
              />
            ) : (
              `No ${entityLabel}s match your search.`
            )
          }
        />
      )}

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => !o && setToDelete(null)}
        title={`Delete ${entityLabel}`}
        description={`"${toDelete ? nameOf(toDelete) : ''}" will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={confirmDelete}
      />
    </Container>
  );
}
